import { useMutation, useQueryClient } from "@tanstack/react-query"
import { extractApiErrorMessage } from "@velizhanin-dev/api-errors"
import { useState } from "react"
import { toast } from "sonner"
import {
	botBuilderKeys,
	type CustomBot,
	createBot,
	createScenario,
	updateBot,
} from "@/entities/bot-builder"
import { defaultScenarioName } from "@/features/choose-bot-scenario"
import { Button } from "@/shared/ui/button"
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/shared/ui/dialog"
import { Field } from "@/shared/ui/form-field"
import { Input } from "@/shared/ui/input"
import { Checkbox } from "@/shared/ui/checkbox"

export function AddBotDialog({
	open,
	onOpenChange,
	onCreated,
}: {
	open: boolean
	onOpenChange: (open: boolean) => void
	onCreated: (bot: CustomBot) => void
}) {
	const queryClient = useQueryClient()
	const [token, setToken] = useState("")
	const [withScenario, setWithScenario] = useState(true)
	const mutation = useMutation({
		// Сценарий создаётся после бота: его имя берётся из имени бота в Telegram.
		mutationFn: async () => {
			const bot = await createBot({ token: token.trim() })
			if (!withScenario) return bot
			const scenario = await createScenario({ name: defaultScenarioName(bot) })
			return updateBot(bot.id, { scenarioId: scenario.id })
		},
		onSuccess: async (bot) => {
			await queryClient.invalidateQueries({ queryKey: botBuilderKeys.bots })
			await queryClient.invalidateQueries({ queryKey: botBuilderKeys.scenarios })
			toast.success(`Бот @${bot.username} добавлен`)
			setToken("")
			onCreated(bot)
		},
		onError: (error) => toast.error(extractApiErrorMessage(error, "Не удалось добавить бота")),
	})

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-md">
				<DialogHeader>
					<DialogTitle>Добавить бота</DialogTitle>
					<DialogDescription>
						Создайте бота в @BotFather и вставьте его токен. Токен хранится зашифрованным.
					</DialogDescription>
				</DialogHeader>
				<form
					className="space-y-4"
					onSubmit={(event) => {
						event.preventDefault()
						if (token.trim()) mutation.mutate()
					}}
				>
					<Field label="Токен" htmlFor="bot-token">
						<Input
							id="bot-token"
							autoFocus
							autoComplete="off"
							spellCheck={false}
							placeholder="123456789:AAH…"
							value={token}
							onChange={(event) => setToken(event.target.value)}
						/>
					</Field>
					<label className="flex cursor-pointer items-start gap-3 rounded-xl border border-border p-3">
						<Checkbox
							checked={withScenario}
							onCheckedChange={(checked) => setWithScenario(checked === true)}
							className="mt-0.5"
						/>
						<span className="text-sm">
							<span className="block font-medium">Сразу создать сценарий</span>
							<span className="block text-muted-foreground">
								Пустой сценарий для воронок и меню этого бота. Готовый можно подключить потом в
								настройках бота.
							</span>
						</span>
					</label>
					<DialogFooter>
						<Button type="submit" disabled={!token.trim() || mutation.isPending}>
							{mutation.isPending ? "Проверяем токен…" : "Добавить"}
						</Button>
					</DialogFooter>
				</form>
			</DialogContent>
		</Dialog>
	)
}
